import {
  BordersStyles,
  Box,
  Button,
  Colors,
  Tag,
  TagColor,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { DeleteAssigmentDocument, DeleteAssigmentMutation } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import DeleteAssignmentModal from 'modules/timeoff/components/Leaves/PolicyAssignments/DeleteAssignmentModal'
import UpsertAssignment from 'modules/timeoff/components/Leaves/PolicyAssignments/UpsertAssignment'
import policies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import { DateField } from './dateField'

enum AssignmentType {
  current = 'current',
  default = 'default',
  future = 'future',
  past = 'past',
}

const BACKGROUND_COLOR_MAP: { [key in AssignmentType]: Colors } = {
  default: 'champagne100',
  current: 'viridian100',
  future: 'grey100',
  past: 'grey000White',
}

const BORDER_COLOR_MAP: {
  selectedAssignment: Colors
  inBetweenAssignments: Colors
} = {
  selectedAssignment: 'viridian1000',
  inBetweenAssignments: 'grey400',
}

interface IPopover {
  onClickEdit: () => void
  onClickDelete: () => void
  isDefaultAssignment: boolean
}

const Popover = ({ onClickEdit, onClickDelete, isDefaultAssignment }: IPopover) => {
  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Button.Decorative type="secondary" iconName="ellipsis" />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="end" disablePortal>
        <PopoverMenu.TouchableOption
          title={i18n.t('time_off.policy_timeline.edit_assignment.title')}
          onClick={onClickEdit}
        />
        <PopoverMenu.TouchableOption
          title={i18n.t('time_off.policy_timeline.delete_assignment.title')}
          danger
          disabled={isDefaultAssignment}
          onClick={onClickDelete}
        />
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

interface IItemTag {
  tagType: AssignmentType
}
const ItemTag = ({ tagType }: IItemTag) => {
  const TAG_ITEM_MAP: { [key in AssignmentType]: { color: TagColor; text: string } } = {
    default: {
      color: TagColor.champagne,
      text: i18n.t('time_off.policy_timeline.tags.default'),
    },
    current: {
      color: TagColor.viridian,
      text: i18n.t('time_off.policy_timeline.tags.current'),
    },
    future: {
      color: TagColor.grey,
      text: i18n.t('time_off.policy_timeline.tags.future'),
    },
    past: {
      color: TagColor.grey,
      text: i18n.t('time_off.policy_timeline.tags.previous'),
    },
  }

  const { color, text } = TAG_ITEM_MAP[tagType]

  return (
    <Box maxWidth="fit-content">
      <Tag type={color}>{text}</Tag>
    </Box>
  )
}

interface Props {
  employeeId: number
  timelineItem: PolicyTimelineItem
  current: boolean
  selected: boolean
  last: boolean
  onSelectTimelineItem: (item: PolicyTimelineItem) => void
  fetchTimeline: () => Promise<void>
}

const Item = ({
  employeeId,
  timelineItem,
  current,
  selected,
  last,
  onSelectTimelineItem,
  fetchTimeline,
}: Props) => {
  const [showUpsertForm, toggleUpsertForm] = useToggle(false)
  const [showDeleteModal, toggleDeleteModal] = useToggle(false)

  const isDefaultAssignment = !timelineItem.policy_assignment_id
  const { start_at, end_at, timeoff_policy_name } = timelineItem
  const policyName =
    timeoff_policy_name || policies.find({ id: timelineItem.timeoff_policy_id })?.attributes.name

  const policyAssignmentId = timelineItem.policy_assignment_id

  const getTagType = (): AssignmentType => {
    if (isDefaultAssignment) return AssignmentType.default
    if (current) return AssignmentType.current
    if (moment(start_at).isAfter(moment())) return AssignmentType.future
    return AssignmentType.past
  }

  const tagType = getTagType()

  const trackDeleteSubmitEvent = () =>
    track(trackingEvents.timeoff.policyAssignments.deletePolicyAssignmentSubmit)

  const [deleteAssignmentMutation] = useMutation(DeleteAssigmentDocument, {
    extractorFn: (mutationData: DeleteAssigmentMutation) =>
      mutationData.timeoffMutations.deleteTimeoffPolicyAssignment,
  })

  const deleteAssignment = async () => {
    trackDeleteSubmitEvent()
    await deleteAssignmentMutation({
      variables: {
        id: policyAssignmentId,
      },
    })

    toggleDeleteModal(false)
    await fetchTimeline()
  }

  const setBackgroundColor = (type: AssignmentType): Colors => {
    return BACKGROUND_COLOR_MAP[type]
  }

  const setBorder = (): BordersStyles | undefined => {
    if (selected) {
      return { all: { width: 's2', style: 'solid', color: BORDER_COLOR_MAP.selectedAssignment } }
    }
    if (!isDefaultAssignment && !current) {
      return { all: { width: 's2', style: 'solid', color: BORDER_COLOR_MAP.inBetweenAssignments } }
    }
  }

  return (
    <Box
      key={policyAssignmentId}
      border={setBorder()}
      background={setBackgroundColor(tagType)}
      borderRadius={{ all: 'abs008' }}
      cursor="pointer"
      onClick={() => onSelectTimelineItem(timelineItem)}
    >
      <Box padding="s16" flexWrap="wrap">
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s8">
            <ItemTag tagType={tagType} />
            <Text size="300" color="grey1000" weight="medium">
              {policyName}
            </Text>
            <DateField
              isDefaultAssignment={isDefaultAssignment}
              start_at={start_at}
              end_at={end_at}
              isLastAssignment={last}
            />
          </Box>

          <Box alignSelf="center">
            <Popover
              onClickEdit={toggleUpsertForm(!showUpsertForm)}
              onClickDelete={toggleDeleteModal(true)}
              isDefaultAssignment={!policyAssignmentId}
            />
          </Box>
        </Box>
        {showDeleteModal && <DeleteAssignmentModal onClickSubmit={deleteAssignment} />}
        {selected && showUpsertForm && (
          <UpsertAssignment
            employeeId={employeeId}
            timelineItem={timelineItem}
            toggleUpsertForm={toggleUpsertForm}
            fetchTimeline={fetchTimeline}
            isDefaultAssignment={!policyAssignmentId}
          ></UpsertAssignment>
        )}
      </Box>
    </Box>
  )
}

export default observer(Item)
